import { useEffect, useState } from "react";

import * as Api from "@modules/api";
import { ContentDetailsParameters } from "@components/layouts/b2c/content/BookSummary";
import { createPageElement } from "@modules/content/creators/createPageElement";
import { usePageData } from "@modules/content/hooks/usePageData";
import { ContentPage } from "@modules/content/types";
import { setAlert, useAppDispatch, useAppSelector } from "@lib/stores";
import {
  clearSelectedPlayerContent,
  getContentEntityItemsById,
  getContentEntityVoiceOvers,
  getDynamicLink,
} from "@lib/stores/content";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { useGetContentProgress } from "@modules/content/hooks/useGetContentProgress";
import { useAppLiveQuery } from "@modules/local/hooks";
import { db } from "@modules/local/db";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";
import { ContentActions } from "@lib/stores/content/slices";

import { ContentDetails } from "./ContentDetails";
import { useGetDynamicLink } from "@lib/stores/content/hooks";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";
import { setStorage } from "@modules/storage/storageHelper";
import { SelectedPlayerContentSource } from "@lib/stores/content/types";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";

export const ContentDetailsPageElement = createPageElement(
  ({ pageId, context, onPageLoading }, ref) => {
    const nextItemSource = getQueryFromUrl("nextItemSource");
    const playlistId = getQueryFromUrl("playlistId");

    const dispatch = useAppDispatch();

    const user = useAppSelector((state) => state.auth.user);

    const [isReaderShown, setIsReaderShown] = useState(false);

    const chapters = useAppSelector(
      ({ content }) => content.visitedPlayerContent.chapters.data
    );

    const { data: dynamicLink } = useGetDynamicLink();

    const {
      pageData,
      refresh,
      isLoading,
      getParameterValues: getParameterValue,
      content,
    } = usePageData<ContentDetailsParameters>(
      {
        pageId: pageId ?? "",
        context,
      },
      ref
    );

    const progress = useAppSelector(({ content: contentState }) =>
      contentState.progresses.find((p) => p.contentEntityId === content?.id)
    );

    const { result: chapterType } = useAppLiveQuery(
      () =>
        chapters?.[0]?.contentEntityTypeId !== undefined
          ? db.entityTypes.get(chapters?.[0]?.contentEntityTypeId)
          : undefined,
      [chapters?.[0]?.contentEntityTypeId]
    );

    useGetContentProgress(Boolean(content?.id), [
      content?.id ?? "",
      ...(chapters?.map((s) => s.id ?? "") ?? []),
    ]);

    const params = getParameterValue([
      "aboutTitle",
      "about",
      "authors",
      "description",
      "descriptionTitle",
      "recommendedTo",
      "recommendedToTitle",
      "readingIcon",
      "readingTitle",
      "listeningIcon",
      "listeningTitle",
      "thumbnail",
      "authorsTitle",
      "categories",
    ]);

    const {
      onPlayerContentSelect,
      onPlayingChapterSelect,
      onFirstPlayingChapterSelect,
      playingChapterId,
      forcedPlayingChapterId,
      onForcedPlayingChapterSelect,
    } = useSelectedPlayerContent();

    const { togglePlayer, isShown, changeIsAutoStart, load, unload } =
      useAudioPlayer();

    const onReadClick = (selectedChapterId?: string) => {
      clearSelectedPlayerContent();

      changeIsAutoStart(!user.autoPlayDisabled);

      setIsReaderShown(chapterType?.requireText === true);

      if (content?.id && progress) {
        onPlayerContentSelect({
          content: {
            id: content.id,
            title: content.title,
            author: content.authors?.join(", "),
            thumbnail: params.thumbnail,
            contentEntityType: content.contentEntityType,
            source: SelectedPlayerContentSource.Details,
          },
          nextItemSource: nextItemSource ? Number(nextItemSource) : undefined,
          playlistId: playlistId ? Number(playlistId) : undefined,
        });

        const chapterId =
          selectedChapterId || progress.currentItem || chapters[0].id;

        onPlayingChapterSelect(chapterId, () => {
          onAudioSelect(chapterId);
        });
        onFirstPlayingChapterSelect(chapterId);

        setStorage({
          key: "LATEST_PLAYING_CONTENT",
          data: {
            content,
            pageId,
            item: params,
          },
        });
      }
    };

    const onAudioSelect = (id: string | undefined) => {
      unload();

      if (progress) {
        const audioUrl = chapters.find((c) => c.id === id)?.signedAudioUrl;

        if (audioUrl) {
          setTimeout(() => {
            load(audioUrl);
          }, 100);
        }
      }
    };

    useEffect(() => {
      if (forcedPlayingChapterId) {
        setIsReaderShown(true);
      }

      if (forcedPlayingChapterId && chapterType?.requireText && progress) {
        onReadClick(forcedPlayingChapterId);

        onForcedPlayingChapterSelect(undefined);
      }
    }, [
      forcedPlayingChapterId,
      chapterType?.requireText,
      progress?.contentEntityId,
    ]);

    useEffect(() => {
      if (content?.id) {
        getContentEntityItemsById({ contentEntityId: content.id });

        getDynamicLink(content.id);
      }
    }, [content?.id]);

    useEffect(() => {
      if (content?.id) {
        dispatch(ContentActions.getContentEntitiesProgressAsync([content.id]));
      }
    }, [playingChapterId, content?.id, dispatch]);

    useEffect(() => {
      if (!pageData?.id || pageData.id !== pageId) {
        onPageLoading && onPageLoading(undefined, isLoading);
      } else {
        onPageLoading && onPageLoading(pageData, isLoading);
      }
    }, [pageData?.id, pageId, isLoading]);

    if (!pageData) return null;

    return (
      <ContentDetails
        content={content || undefined}
        chapters={chapters}
        params={params}
        playingChapterId={playingChapterId}
        progress={progress}
        menu={{
          onShareClick: () => {
            if (navigator && dynamicLink?.link) {
              navigator.clipboard.writeText(dynamicLink.link);

              setAlert({
                show: true,
                for: "page",
                type: AlertType.SUCCESS,
                messages: [
                  {
                    code: 0,
                    field: null,
                    message: translate(
                      "content-details.messages.the link has been copied successfully"
                    ),
                  },
                ],
              });
            }
          },
        }}
        onListen={async () => {
          clearSelectedPlayerContent();

          togglePlayer(true);
          changeIsAutoStart(!user.autoPlayDisabled);

          if (content?.id && progress) {
            onPlayerContentSelect({
              content: {
                id: content.id,
                title: content.title,
                author: content.authors?.join(", "),
                thumbnail: params.thumbnail,
                contentEntityType: content.contentEntityType,
                source: SelectedPlayerContentSource.Details,
              },
              nextItemSource: nextItemSource
                ? Number(nextItemSource)
                : undefined,
              playlistId: playlistId ? Number(playlistId) : undefined,
            });

            const chapterId = progress.currentItem || chapters[0].id;

            onPlayingChapterSelect(chapterId, () => {
              onAudioSelect(chapterId);
            });
            onFirstPlayingChapterSelect(chapterId);

            const voiceOversResponse = await getContentEntityVoiceOvers(
              content?.id || ""
            );

            let voiceOvers: Api.Server.ContentEntityVoiceOverOutput[] = [];
            if (voiceOversResponse.meta.requestStatus === "fulfilled")
              voiceOvers = voiceOversResponse.payload;
            const currentVoiceOver = voiceOvers.find(
              (v) => v.narratorId === chapters[0].narratorId
            );

            setStorage({
              key: "LATEST_PLAYING_CONTENT",
              data: {
                content,
                pageId,
                item: params,
                voiceOvers: {
                  data: voiceOvers,
                  currentVoiceOver,
                },
              },
            });
          }
        }}
        onListenToChapter={() => {
          togglePlayer(true);
          changeIsAutoStart(true);
        }}
        onRead={onReadClick}
        readerShown={isReaderShown}
        onReaderClose={() => {
          setIsReaderShown(false);
        }}
      />
    );
  }
);

export const ContentDetailsPage: ContentPage = {
  identifier: "PG:CONTENT:DETAILS",
  page: ContentDetailsPageElement,
};
